import type { UserAccessSnapshot } from '@/features/commandDeck/contracts/types'
import { FIREBASE_ENABLED, getDbOrThrow } from '@/lib/firebase'

import { VISITOR_ACCESS_SNAPSHOT } from './constants'
import {
  buildLegacyProfile,
  mapLegacyProfileToAccessSnapshot,
  type FirestoreUserAccessFields,
} from './mapLegacyProfileToAccessSnapshot'
import type { AuthSessionLegacyProfile } from './types'

export interface LoadedUserAccess {
  snapshot: UserAccessSnapshot
  legacy: AuthSessionLegacyProfile
}

export function parseUserAccessDocument(
  data: Record<string, unknown> | undefined,
  fallbackDisplayName?: string
): LoadedUserAccess {
  const fields = (data ?? {}) as FirestoreUserAccessFields
  const legacyBuilt = buildLegacyProfile(fields)
  const snapshot = mapLegacyProfileToAccessSnapshot(fields, fallbackDisplayName)

  return {
    snapshot,
    legacy: {
      planId: legacyBuilt.planId,
      roles: legacyBuilt.roles,
      browseOnly: legacyBuilt.browseOnly,
      displayName: legacyBuilt.displayName ?? snapshot.displayName,
    },
  }
}

/**
 * Read-only Firestore load for users/{uid}. Never writes accessState.
 */
export async function loadUserAccessSnapshot(
  uid: string,
  fallbackDisplayName?: string
): Promise<LoadedUserAccess> {
  if (!FIREBASE_ENABLED) {
    return parseUserAccessDocument(undefined, fallbackDisplayName)
  }

  const { doc, getDoc } = await import('firebase/firestore')
  const db = getDbOrThrow()
  const snap = await getDoc(doc(db, 'users', uid))

  if (!snap.exists()) {
    return parseUserAccessDocument(undefined, fallbackDisplayName)
  }

  return parseUserAccessDocument(snap.data() as Record<string, unknown>, fallbackDisplayName)
}

export function failClosedVisitorAccess(
  fallbackDisplayName?: string
): LoadedUserAccess {
  return {
    snapshot: {
      ...VISITOR_ACCESS_SNAPSHOT,
      displayName: fallbackDisplayName ?? VISITOR_ACCESS_SNAPSHOT.displayName,
    },
    legacy: {},
  }
}
